import { z } from 'zod'
import { parseDay } from '@/features/calendar/lib/dates'
import { fromLocalInput, toLocalInput } from '@/features/channels/lib/item-form'
import type { Schedule, ScheduleRecurrence, ScheduleTargetType } from '@/types/api'
import { ALL_DAYS, parseTime } from './rule'

/**
 * Schedule editor form ⇄ API payload (API.md Phase 7 "Schedule input").
 * Plan gates without `advanced_scheduling`: priority 50, timezone `display`, no `monthly_dom`.
 */

export const DEFAULT_PRIORITY = 50

export type ScopedTargetType = Exclude<ScheduleTargetType, 'tenant'>
const SCOPED_TYPES = new Set<string>(['location', 'display_group', 'tag', 'display'])
export const TARGET_TYPE_LABELS: Record<ScheduleTargetType, string> = {
  tenant: 'All displays',
  location: 'Locations',
  display_group: 'Groups',
  tag: 'Tags',
  display: 'Displays',
}
export const TARGET_TYPE_SINGULAR: Record<ScheduleTargetType, string> = {
  tenant: 'All displays',
  location: 'Location',
  display_group: 'Group',
  tag: 'Tag',
  display: 'Display',
}

export interface TargetSelection {
  all: boolean
  location: string[]
  display_group: string[]
  tag: string[]
  display: string[]
}

const optionalDate = z.string().trim().refine((v) => v === '' || parseDay(v) !== null, 'Enter a valid date')
const optionalDateTime = z.string().trim().refine((v) => v === '' || !Number.isNaN(new Date(v).getTime()), 'Enter a valid date and time')

export const scheduleFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(160),
    content_type: z.enum(['channel', 'slideshow']),
    content_id: z.string(),
    campaign_id: z.string().nullable(),
    targets: z.object({
      all: z.boolean(),
      location: z.array(z.string()),
      display_group: z.array(z.string()),
      tag: z.array(z.string()),
      display: z.array(z.string()),
    }),
    recurrence: z.enum(['once', 'daily', 'weekly', 'monthly_dom']),
    start_date: optionalDate,
    end_date: optionalDate,
    all_day: z.boolean(),
    start_time: z.string(),
    end_time: z.string(),
    /** End time is `24:00` (until midnight). */
    until_midnight: z.boolean(),
    weekdays: z.number().int().min(0).max(ALL_DAYS),
    days_of_month: z.array(z.number().int().min(1).max(31)),
    timezone: z.string().min(1),
    valid_from: optionalDateTime,
    valid_until: optionalDateTime,
    priority: z.number().int().min(0).max(100),
    is_enabled: z.boolean(),
    /** Signetix Logic: library rule that must hold (null = unconditional). */
    condition_rule_id: z.string().nullable(),
    condition_unknown_as: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (!v.content_id) ctx.addIssue({ code: 'custom', path: ['content_id'], message: v.content_type === 'channel' ? 'Choose a channel' : 'Choose a slideshow' })
    if (targetCount(v.targets) === 0) ctx.addIssue({ code: 'custom', path: ['targets'], message: 'Choose where it plays' })
    if (targetCount(v.targets) > 200) ctx.addIssue({ code: 'custom', path: ['targets'], message: 'At most 200 targets' })
    if (v.recurrence === 'once' && !v.start_date) ctx.addIssue({ code: 'custom', path: ['start_date'], message: 'Pick the date' })
    if (v.recurrence !== 'once' && v.start_date && v.end_date && v.end_date < v.start_date) ctx.addIssue({ code: 'custom', path: ['end_date'], message: 'Must be on or after the start date' })
    if (!v.all_day) {
      if (parseTime(v.start_time) === null) ctx.addIssue({ code: 'custom', path: ['start_time'], message: 'Enter a start time' })
      if (!v.until_midnight && parseTime(v.end_time) === null) ctx.addIssue({ code: 'custom', path: ['end_time'], message: 'Enter an end time' })
    }
    if (v.recurrence !== 'once' && v.weekdays === 0) ctx.addIssue({ code: 'custom', path: ['weekdays'], message: 'Pick at least one day' })
    if (v.recurrence === 'monthly_dom' && v.days_of_month.length === 0) ctx.addIssue({ code: 'custom', path: ['days_of_month'], message: 'Pick at least one day of the month' })
    if (v.valid_from && v.valid_until && new Date(v.valid_until).getTime() <= new Date(v.valid_from).getTime())
      ctx.addIssue({ code: 'custom', path: ['valid_until'], message: 'Must be after “valid from”' })
  })

export type ScheduleFormValues = z.infer<typeof scheduleFormSchema>

export function targetCount(t: TargetSelection): number {
  return t.all ? 1 : t.location.length + t.display_group.length + t.tag.length + t.display.length
}

/** Prefill for "new schedule" (optionally from a calendar slot / campaign / display). */
export interface SchedulePrefill {
  start_date?: string
  start_time?: string
  end_time?: string
  recurrence?: ScheduleRecurrence
  campaign_id?: string | null
  content_type?: 'channel' | 'slideshow'
  content_id?: string
  targets?: Partial<TargetSelection>
  name?: string
}

export function scheduleFormDefaults(schedule?: Schedule | null, prefill: SchedulePrefill = {}): ScheduleFormValues {
  if (!schedule) {
    const endTime = prefill.end_time ?? '17:00'
    return {
      name: prefill.name ?? '',
      content_type: prefill.content_type ?? 'channel',
      content_id: prefill.content_id ?? '',
      campaign_id: prefill.campaign_id ?? null,
      targets: { all: !prefill.targets, location: [], display_group: [], tag: [], display: [], ...prefill.targets },
      recurrence: prefill.recurrence ?? 'weekly',
      start_date: prefill.start_date ?? '',
      end_date: '',
      all_day: false,
      start_time: prefill.start_time ?? '09:00',
      end_time: endTime === '24:00' ? '' : endTime,
      until_midnight: endTime === '24:00',
      weekdays: ALL_DAYS,
      days_of_month: [],
      timezone: 'display',
      valid_from: '',
      valid_until: '',
      priority: DEFAULT_PRIORITY,
      is_enabled: true,
      condition_rule_id: null,
      condition_unknown_as: false,
    }
  }
  const targets: TargetSelection = { all: false, location: [], display_group: [], tag: [], display: [] }
  for (const t of schedule.targets) {
    if (t.type === 'tenant') targets.all = true
    else if (t.id && SCOPED_TYPES.has(t.type)) targets[t.type as ScopedTargetType].push(t.id)
  }
  const start = schedule.start_time ?? null
  const end = schedule.end_time ?? null
  const allDay = (start === null || start === '00:00') && (end === null || end === '00:00' || end === '24:00')
  return {
    name: schedule.name,
    content_type: schedule.content.type,
    content_id: schedule.content.id,
    campaign_id: schedule.campaign?.id ?? null,
    targets,
    recurrence: schedule.recurrence,
    start_date: schedule.start_date ?? '',
    end_date: schedule.end_date ?? '',
    all_day: allDay,
    start_time: allDay ? '09:00' : (start ?? '00:00'),
    end_time: allDay ? '17:00' : end === '24:00' || end === null ? '' : end,
    until_midnight: !allDay && (end === '24:00' || end === null),
    weekdays: schedule.weekdays,
    days_of_month: schedule.days_of_month ?? [],
    timezone: schedule.timezone || 'display',
    valid_from: toLocalInput(schedule.valid_from),
    valid_until: toLocalInput(schedule.valid_until),
    priority: schedule.priority,
    is_enabled: schedule.is_enabled,
    condition_rule_id: schedule.condition?.rule_id ?? null,
    condition_unknown_as: schedule.condition?.unknown_as ?? false,
  }
}

export interface ScheduleInput {
  name: string
  content_type: 'channel' | 'slideshow'
  channel_id?: string | null
  slideshow_id?: string | null
  campaign_id: string | null
  priority: number
  start_date: string | null
  end_date: string | null
  start_time: string | null
  end_time: string | null
  weekdays: number
  recurrence: ScheduleRecurrence
  days_of_month: number[] | null
  timezone: string
  valid_from: string | null
  valid_until: string | null
  is_enabled: boolean
  targets: { type: ScheduleTargetType; id?: string }[]
  condition_rule_id?: string | null
  condition_unknown_as?: boolean
}

/** Targets in a stable order (the order 422 `targets.N.*` indexes refer to). */
export function targetsPayload(t: TargetSelection): ScheduleInput['targets'] {
  if (t.all) return [{ type: 'tenant' }]
  return (['location', 'display_group', 'tag', 'display'] as const).flatMap((type) => t[type].map((id) => ({ type, id })))
}

export function scheduleFormToPayload(v: ScheduleFormValues): ScheduleInput {
  const once = v.recurrence === 'once'
  return {
    name: v.name.trim(),
    content_type: v.content_type,
    ...(v.content_type === 'channel' ? { channel_id: v.content_id, slideshow_id: null } : { slideshow_id: v.content_id, channel_id: null }),
    campaign_id: v.campaign_id,
    priority: v.priority,
    start_date: v.start_date || null,
    // A one-off runs on its start date only; the engine warns when end_date differs.
    end_date: once ? null : v.end_date || null,
    start_time: v.all_day ? null : v.start_time,
    end_time: v.all_day ? null : v.until_midnight ? '24:00' : v.end_time,
    weekdays: once ? ALL_DAYS : v.weekdays,
    recurrence: v.recurrence,
    days_of_month: v.recurrence === 'monthly_dom' ? [...v.days_of_month].sort((a, b) => a - b) : null,
    timezone: v.timezone,
    valid_from: fromLocalInput(v.valid_from),
    valid_until: fromLocalInput(v.valid_until),
    is_enabled: v.is_enabled,
    targets: targetsPayload(v.targets),
    condition_rule_id: v.condition_rule_id,
    condition_unknown_as: v.condition_rule_id ? v.condition_unknown_as : false,
  }
}

/** Which inputs need `advanced_scheduling`. */
export function advancedFeaturesUsed(v: Pick<ScheduleFormValues, 'priority' | 'timezone' | 'recurrence'>): ('priority' | 'timezone' | 'recurrence')[] {
  const out: ('priority' | 'timezone' | 'recurrence')[] = []
  if (v.priority !== DEFAULT_PRIORITY) out.push('priority')
  if (v.timezone !== 'display') out.push('timezone')
  if (v.recurrence === 'monthly_dom') out.push('recurrence')
  return out
}

const FIELD_ALIASES: Record<string, keyof ScheduleFormValues> = {
  channel_id: 'content_id',
  slideshow_id: 'content_id',
  content_type: 'content_id',
  days_of_month: 'days_of_month',
  condition_unknown_as: 'condition_rule_id',
}

/**
 * Maps a 422 `errors` map onto form fields. `targets.N.id` → `targets`, prefixed with the
 * target's kind and name ("Display “Lobby”: The selected target is invalid.").
 */
export function mapScheduleErrors(
  errors: Record<string, string[]>,
  sentTargets: ScheduleInput['targets'],
  targetName: (type: ScheduleTargetType, id: string | undefined) => string | null = () => null,
): { fields: Partial<Record<keyof ScheduleFormValues, string>>; unmatched: string[] } {
  const fields: Partial<Record<keyof ScheduleFormValues, string>> = {}
  const unmatched: string[] = []
  const known = new Set<string>(Object.keys(scheduleFormSchema.shape))
  for (const [key, messages] of Object.entries(errors)) {
    const message = messages[0]
    if (!message) continue
    const target = /^targets\.(\d+)(?:\.(\w+))?$/.exec(key)
    if (target) {
      const sent = sentTargets[Number(target[1])]
      const name = sent ? targetName(sent.type, sent.id) : null
      const label = sent ? `${TARGET_TYPE_SINGULAR[sent.type]}${name ? ` “${name}”` : ''}: ` : ''
      fields.targets ??= `${label}${message}`
      continue
    }
    const base = key.split('.')[0] ?? key
    const field = FIELD_ALIASES[base] ?? (known.has(base) ? (base as keyof ScheduleFormValues) : null)
    if (field) fields[field] ??= message
    else unmatched.push(message)
  }
  return { fields, unmatched }
}
